import { Component, inject, computed, ChangeDetectionStrategy, viewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';

import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { Popover, PopoverModule } from 'primeng/popover';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';

import { NotificationActions } from 'src/app/store/application/notification/notification.actions';
import {
    selectUnreadNotifications,
    selectUnreadCount,
    selectNotificationHistory,
} from 'src/app/store/application/notification/notification.selectors';
import { Notification, NotificationType, NotificationReadStatus } from 'src/api-service';

@Component({
    selector: 'app-notification-bell',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DatePipe,
        TranslateModule,
        OverlayBadgeModule,
        PopoverModule,
        DividerModule,
        TooltipModule,
    ],
    templateUrl: './notification-bell.component.html',
    styleUrl: './notification-bell.component.scss',
})
export class NotificationBellComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly store = inject(Store);

    // =========================================================================
    // VIEW CHILDREN
    // =========================================================================
    private readonly popover = viewChild<Popover>('notificationPopover');

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    public readonly unread = toSignal(this.store.select(selectUnreadNotifications), { initialValue: [] });
    public readonly history = toSignal(this.store.select(selectNotificationHistory), { initialValue: [] });
    public readonly unreadCount = toSignal(this.store.select(selectUnreadCount), { initialValue: 0 });

    // =========================================================================
    // COMPUTED
    // =========================================================================
    public readonly hasUnread = computed(() => this.unreadCount() > 0);

    /** Filter out history items without valid notification data */
    public readonly validHistory = computed(() =>
        this.history().filter(item => item.notification?.id && item.notification?.title)
    );

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public getTypeIcon(type: NotificationType | undefined): string {
        switch (type) {
            case NotificationType.Release:
                return '🚀';
            case NotificationType.Hotfix:
                return '🔧';
            case NotificationType.Info:
                return 'ℹ️';
            case NotificationType.Maintenance:
                return '🔨';
            case NotificationType.Warning:
                return '⚠️';
            default:
                return '📢';
        }
    }

    public openNotification(notification: Notification): void {
        this.popover()?.hide();
        this.store.dispatch(NotificationActions.selectNotification({ id: notification.id, isUnread: true }));
    }

    public openHistoryItem(item: NotificationReadStatus): void {
        if (item.notification) {
            this.popover()?.hide();
            this.store.dispatch(NotificationActions.selectNotification({ id: item.notification.id, isUnread: false }));
        }
    }
}
